import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { ResponsiveTable } from "../components/ResponsiveTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../foundation/altzero_base/base/components/ui/alert-dialog";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import { Checkbox } from "../../../foundation/altzero_base/base/components/ui/checkbox";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  ArrowLeft,
  Mail,
  Phone,
  FileText,
  Eye,
  MapPin,
  Zap,
  DollarSign,
} from "lucide-react";
import ScopingAILayout from "../components/ScopingAILayout";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import {
  getClients,
  createClient,
  updateClient,
  updateClientBillVal,
  deleteClient,
  Client,
} from "../services/clientService";
import { getTendersByClientId, Tender } from "../services/tenderService";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import TenderSitesOverview from "../components/TenderSitesOverview";
import SiteOffersView from "../components/SiteOffersView";

interface TenderDetails {
  tender: Tender;
  sites: any[];
  offers: any[];
}

type DialogView = 'sites' | 'offers';

interface TenderSite {
  id: string;
  name: string;
  nmi: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  addr_country?: string;
  annual_kwh_peak?: number;
  annual_kwh_offpeak?: number;
  annual_kwh_shoulder?: number;
  abn?: string;
  acn?: string;
  due_at?: string;
  current_contract_expiry?: string;
  current_meter_provider?: string;
  current_retailer?: string;
  dma_provider?: string;
  retailer_comms?: string;
  create_dma?: boolean;
  request_meter_data?: boolean;
  request_bills?: boolean;
  acquire_ced?: boolean;
}

export default function ClientsManagement() {
  const { toast } = useToast();
  const { can } = usePermissions();
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_name: "",
    abn: "",
    channel_partner: "",
    sf_account_id: "",
    email: "",
    phone: "",
    industry: "",
    broker: "",
    shield_platinum: false,
  });

  // Client Tenders State
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientTenders, setClientTenders] = useState<Tender[]>([]);
  const [showClientTenders, setShowClientTenders] = useState(false);
  const [loadingClientTenders, setLoadingClientTenders] = useState(false);
  const [selectedTender, setSelectedTender] = useState<TenderDetails | null>(null);
  const [showTenderDetails, setShowTenderDetails] = useState(false);
  const [loadingTenderDetails, setLoadingTenderDetails] = useState(false);
  const [dialogView, setDialogView] = useState<DialogView>('sites');
  const [selectedSite, setSelectedSite] = useState<TenderSite | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchQuery, clients]);

  const loadClients = async () => {
    setLoading(true);
    const result = await getClients();
    if (result.success && result.data) {
      setClients(result.data);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to load clients",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const filterClients = () => {
    if (!searchQuery.trim()) {
      setFilteredClients(clients);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = clients.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        client.abn?.toLowerCase().includes(query) ||
        client.channel_partner?.toLowerCase().includes(query) ||
        client.sf_account_id?.toLowerCase().includes(query) ||
        client.extn?.email?.toLowerCase().includes(query) ||
        client.extn?.phone?.toLowerCase().includes(query)
    );
    setFilteredClients(filtered);
  };

  const handleOpenDialog = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name,
        contact_name: "",
        abn: client.abn || "",
        channel_partner: client.channel_partner || "",
        sf_account_id: client.sf_account_id || "",
        email: client.extn?.email || "",
        phone: client.extn?.phone || "",
        industry: client.extn?.industry || "",
        broker: client.broker || "",
        shield_platinum: client.shield_platinum ?? false,
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: "",
        contact_name: "",
        abn: "",
        channel_partner: "",
        sf_account_id: "",
        email: "",
        phone: "",
        industry: "",
        broker: "",
        shield_platinum: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingClient(null);
    setFormData({
      name: "",
      contact_name: "",
      abn: "",
      channel_partner: "",
      sf_account_id: "",
      email: "",
      phone: "",
      industry: "",
      broker: "",
      shield_platinum: false,
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Client name is required",
        variant: "destructive",
      });
      return;
    }

    const result = editingClient
      ? await updateClient(editingClient.id, formData)
      : await createClient(formData);

    if (result.success) {
      toast({
        title: "Success",
        description: `Client ${editingClient ? "updated" : "created"} successfully`,
      });
      handleCloseDialog();
      loadClients();
    } else {
      toast({
        title: "Error",
        description: result.error || `Failed to ${editingClient ? "update" : "create"} client`,
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (clientId: string) => {
    setDeletingClientId(clientId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingClientId) return;

    const result = await deleteClient(deletingClientId);
    if (result.success) {
      toast({
        title: "Success",
        description: "Client deleted successfully",
      });
      loadClients();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete client",
        variant: "destructive",
      });
    }
    setIsDeleteDialogOpen(false);
    setDeletingClientId(null);
  };

  // Client Tenders Functions
  const handleViewClientTenders = async (client: Client) => {
    setSelectedClient(client);
    setLoadingClientTenders(true);
    setShowClientTenders(true);
    setShowTenderDetails(false);

    try {
      const result = await getTendersByClientId(client.id);
      if (result.success && result.data) {
        setClientTenders(result.data);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to load client tenders",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("Error loading client tenders:", error);
      toast({
        title: "Error",
        description: "Failed to load client tenders",
        variant: "destructive",
      });
    }
    setLoadingClientTenders(false);
  };

  const handleBackToClients = () => {
    setShowClientTenders(false);
    setShowTenderDetails(false);
    setSelectedClient(null);
    setClientTenders([]);
    setSelectedTender(null);
    setSelectedSite(null);
  };

  const handleBillValChange = async (clientId: string, value: 'active' | 'inactive') => {
    setClients((prev) =>
      prev.map((c) => (c.id === clientId ? { ...c, bill_val: value } : c))
    );
    const result = await updateClientBillVal(clientId, value);
    if (!result.success) {
      toast({
        title: "Error",
        description: result.error || "Failed to update bill validation status",
        variant: "destructive",
      });
      loadClients();
    }
  };

  const handleViewTenderDetails = async (tender: Tender) => {
    setLoadingTenderDetails(true);
    setShowTenderDetails(true);
    setDialogView('sites');
    setSelectedSite(null);

    try {
      // Fetch tender sites with document_file_paths from site table
      const { data: sites, error: sitesError } = await supabase
        .from("tender_site")
        .select(`
          *,
          site:site_id (
            document_file_paths,
            retailer_notes
          )
        `)
        .eq("tender_id", tender.id)
        .order("created_at", { ascending: true });

      if (sitesError) throw sitesError;

      // Fetch offers for this tender
      const { data: offers, error: offersError } = await supabase
        .from("offer")
        .select(`
          *,
          retailer:retailer_id (
            id,
            name,
            logo
          )
        `)
        .eq("tender_id", tender.id)
        .order("created_at", { ascending: true });

      if (offersError) throw offersError;

      // Map sites to include document_file_paths and retailer_notes from the joined site table
      const mappedSites = (sites || []).map((site: any) => ({
        ...site,
        document_file_paths: site.site?.document_file_paths || null,
        retailer_notes: site.site?.retailer_notes || null,
      }));

      setSelectedTender({
        tender,
        sites: mappedSites,
        offers: offers || [],
      });
    } catch (error: any) {
      console.error("Error loading tender details:", error);
      toast({
        title: "Error",
        description: "Failed to load tender details",
        variant: "destructive",
      });
    }
    setLoadingTenderDetails(false);
  };

  const handleBackToClientTenders = () => {
    setShowTenderDetails(false);
    setSelectedTender(null);
    setSelectedSite(null);
    setDialogView('sites');
  };

  const handleViewOffers = (site: TenderSite) => {
    setSelectedSite(site);
    setDialogView('offers');
  };

  const handleBackToSites = () => {
    setSelectedSite(null);
    setDialogView('sites');
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="space-y-4 md:space-y-6">
          {/* Header */}
          <div>
            <Link
              to="/tender/resource-library"
              className="flex items-center text-muted-foreground hover:text-foreground mb-4 text-sm md:text-base"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Resource Library
            </Link>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="bg-primary/10 p-2 md:p-3 rounded-lg">
                  <Building2 className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold">Clients</h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Manage your client organizations
                  </p>
                </div>
              </div>
              {can('client.create') && (
                <Button onClick={() => handleOpenDialog()} className="w-full md:w-auto">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Client
                </Button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search clients by name, ABN, or channel partner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Clients Table - Hide when showing client tenders */}
          {!showClientTenders && (
            <Card>
              <CardHeader>
                <CardTitle>All Clients ({filteredClients.length})</CardTitle>
                <CardDescription>
                  Client organizations in your system
                </CardDescription>
              </CardHeader>
              <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading clients...
                </div>
              ) : (
                <ResponsiveTable
                  data={filteredClients}
                  columns={[
                    {
                      key: "name",
                      label: "Name",
                      render: (client) => (
                        <span className="font-medium">{client.name}</span>
                      ),
                    },
                    {
                      key: "abn",
                      label: "ABN",
                      render: (client) =>
                        client.abn || (
                          <span className="text-muted-foreground">—</span>
                        ),
                    },
                    {
                      key: "sf_account_id",
                      label: "SF Account ID",
                      render: (client) =>
                        client.sf_account_id ? (
                          <span className="font-mono text-xs">{client.sf_account_id}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        ),
                    },
                    {
                      key: "channel_partner",
                      label: "Channel Partner",
                      render: (client) =>
                        client.channel_partner || (
                          <span className="text-muted-foreground">—</span>
                        ),
                    },
                    {
                      key: "contact",
                      label: "Contact",
                      render: (client) => (
                        <div className="text-sm">
                          {client.extn?.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {client.extn.email}
                            </div>
                          )}
                          {client.extn?.phone && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="w-3 h-3" />
                              {client.extn.phone}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: "broker",
                      label: "Broker",
                      render: (client) =>
                        client.broker || (
                          <span className="text-muted-foreground">—</span>
                        ),
                    },
                    {
                      key: "shield_platinum",
                      label: "Shield Platinum",
                      render: (client) =>
                        client.shield_platinum ? (
                          <Badge variant="default">Shield Platinum</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (client) =>
                        new Date(client.created_at).toLocaleDateString(),
                    },
                    {
                      key: "bill_val",
                      label: "Bill Validation",
                      render: (client) => {
                        const billVal = client.bill_val ?? 'inactive';
                        const tone =
                          billVal === 'active'
                            ? 'bg-green-50 text-green-700 border-green-300'
                            : 'bg-gray-50 text-gray-500 border-gray-200';

                        // Bill validation is a client field, so it is gated on the same
                        // permission as the Edit action below. Without this gate a broker
                        // could flip any client active/inactive straight from the list.
                        if (!can('client.edit')) {
                          return (
                            <span
                              className={`inline-block text-xs font-medium px-2 py-1 rounded border ${tone}`}
                            >
                              {billVal === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          );
                        }

                        return (
                          <select
                            value={billVal}
                            onChange={(e) =>
                              handleBillValChange(client.id, e.target.value as 'active' | 'inactive')
                            }
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs font-medium px-2 py-1 rounded border cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary ${tone}`}
                          >
                            <option value="inactive">Inactive</option>
                            <option value="active">Active</option>
                          </select>
                        );
                      },
                    },
                  ]}
                  actions={(client) => (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewClientTenders(client)}
                        title="View Tenders"
                        className="h-8 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        <span className="text-xs font-medium">Tenders</span>
                      </Button>
                      {can('client.edit') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(client)}
                          title="Edit Client"
                          className="h-8 w-8 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {can('client.delete') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(client.id)}
                          title="Delete Client"
                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                  mobileCardRender={(client) => (
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{client.name}</h3>
                          {client.abn && (
                            <p className="text-sm text-muted-foreground mt-1">
                              ABN: {client.abn}
                            </p>
                          )}
                        </div>
                      </div>
                      {client.channel_partner && (
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span>{client.channel_partner}</span>
                        </div>
                      )}
                      {client.extn?.email && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-4 h-4" />
                          {client.extn.email}
                        </div>
                      )}
                      {client.extn?.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="w-4 h-4" />
                          {client.extn.phone}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        Created: {new Date(client.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                  emptyMessage={
                    searchQuery
                      ? "No clients found matching your search"
                      : "No clients yet. Add your first client to get started."
                  }
                />
              )}
            </CardContent>
          </Card>
          )}

          {/* Client Tenders View */}
          {showClientTenders && selectedClient && (
            <Card>
              <CardHeader className="pb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3 text-2xl">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <span className="text-gray-900">{selectedClient.name}</span>
                        <span className="text-lg text-muted-foreground ml-2">• Tenders</span>
                      </div>
                    </CardTitle>
                    <CardDescription className="mt-2 text-base">
                      Manage all tenders for this client organization
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleBackToClients}
                    className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Clients
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Tender Details View */}
                {showTenderDetails && selectedTender ? (
                  <div className="space-y-6">
                    {/* Tender Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          {selectedTender.tender.opportunity_name || "Tender Details"}
                        </h2>
                        <div className="flex items-center gap-4 mt-3">
                          <Badge variant={
                            selectedTender.tender.status === 'won' ? 'default' :
                            selectedTender.tender.status === 'submitted' ? 'secondary' :
                            selectedTender.tender.status === 'lost' ? 'destructive' : 'outline'
                          } className="px-3 py-1">
                            {selectedTender.tender.status.toUpperCase()}
                          </Badge>
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <span>Created:</span>
                            <span className="font-medium">
                              {new Date(selectedTender.tender.created_at).toLocaleDateString()}
                            </span>
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={handleBackToClientTenders}
                        className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Tenders
                      </Button>
                    </div>

                    {/* Dynamic Content Based on View */}
                    {dialogView === 'sites' ? (
                      <TenderSitesOverview
                        sites={selectedTender.sites}
                        offers={selectedTender.offers}
                        onViewOffers={handleViewOffers}
                        tenderId={selectedTender.tender.id}
                      />
                    ) : dialogView === 'offers' && selectedSite ? (
                      <SiteOffersView
                        site={selectedSite}
                        offers={selectedTender.offers}
                        onBack={handleBackToSites}
                      />
                    ) : null}
                  </div>
                ) : (
                  /* Client Tenders List */
                  <>
                    {loadingClientTenders ? (
                      <div className="flex flex-col items-center justify-center py-12 space-y-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <div className="text-muted-foreground font-medium">Loading tenders...</div>
                      </div>
                    ) : (
                      <ResponsiveTable
                        data={clientTenders}
                        columns={[
                          {
                            key: "opportunity_name",
                            label: "Tender Name",
                            render: (tender) => (
                              <span className="font-medium">{tender.opportunity_name || "—"}</span>
                            ),
                          },
                          {
                            key: "status",
                            label: "Status",
                            render: (tender) => (
                              <Badge variant={
                                tender.status === 'won' ? 'default' :
                                tender.status === 'submitted' ? 'secondary' :
                                tender.status === 'lost' ? 'destructive' : 'outline'
                              }>
                                {tender.status.toUpperCase()}
                              </Badge>
                            ),
                          },
                          {
                            key: "account_name",
                            label: "Account",
                            render: (tender) => tender.account_name || "—",
                          },
                          {
                            key: "due_at",
                            label: "Due Date",
                            render: (tender) =>
                              tender.due_at ? new Date(tender.due_at).toLocaleDateString() : "—",
                          },
                          {
                            key: "created_at",
                            label: "Created",
                            render: (tender) =>
                              new Date(tender.created_at).toLocaleDateString(),
                          },
                        ]}
                        actions={(tender) => (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewTenderDetails(tender)}
                            title="View Details"
                            className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            <span className="text-xs font-medium">Details</span>
                          </Button>
                        )}
                        mobileCardRender={(tender) => (
                          <div className="space-y-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg text-gray-900">
                                  {tender.opportunity_name || "Untitled Tender"}
                                </h3>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant={
                                    tender.status === 'won' ? 'default' :
                                    tender.status === 'submitted' ? 'secondary' :
                                    tender.status === 'lost' ? 'destructive' : 'outline'
                                  } className="px-2 py-1">
                                    {tender.status.toUpperCase()}
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewTenderDetails(tender)}
                                className="ml-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                <span className="text-xs">Details</span>
                              </Button>
                            </div>
                            {tender.account_name && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Building2 className="w-4 h-4 text-gray-400" />
                                <span>{tender.account_name}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between text-sm">
                              {tender.due_at && (
                                <div className="text-muted-foreground">
                                  <span className="font-medium">Due:</span> {new Date(tender.due_at).toLocaleDateString()}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground">
                                Created: {new Date(tender.created_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        )}
                        emptyMessage="No tenders found for this client"
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Edit Client" : "Add New Client"}
            </DialogTitle>
            <DialogDescription>
              {editingClient
                ? "Update client information"
                : "Add a new client organization"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Client Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Acme Corporation"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="contact_name">Primary Contact Name</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) =>
                  setFormData({ ...formData, contact_name: e.target.value })
                }
                placeholder="e.g., John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="abn">ABN</Label>
              <Input
                id="abn"
                value={formData.abn}
                onChange={(e) =>
                  setFormData({ ...formData, abn: e.target.value })
                }
                placeholder="11 digit ABN"
                maxLength={11}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="channel_partner">Channel Partner</Label>
              <Input
                id="channel_partner"
                value={formData.channel_partner}
                onChange={(e) =>
                  setFormData({ ...formData, channel_partner: e.target.value })
                }
                placeholder="Partner name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="contact@client.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="+61 4XX XXX XXX"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) =>
                  setFormData({ ...formData, industry: e.target.value })
                }
                placeholder="e.g., Manufacturing"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sf_account_id">Salesforce Account ID</Label>
              <Input
                id="sf_account_id"
                value={formData.sf_account_id}
                onChange={(e) =>
                  setFormData({ ...formData, sf_account_id: e.target.value })
                }
                placeholder="SF Account ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="broker">Broker</Label>
              <Input
                id="broker"
                value={formData.broker}
                onChange={(e) =>
                  setFormData({ ...formData, broker: e.target.value })
                }
                placeholder="Broker name"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Checkbox
                id="shield_platinum"
                checked={formData.shield_platinum}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, shield_platinum: checked === true })
                }
              />
              <Label htmlFor="shield_platinum">Shield Platinum</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingClient ? "Update" : "Create"} Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this client and all associated contacts and sites.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScopingAILayout>
  );
}

